import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Leaf,
  User,
  MessageSquare,
  Settings,
  LogOut,
  Mail,
  Clock,
  Send,
  CheckCircle,
  Edit,
  Save,
  X,
  DollarSign,
  Calendar,
  Star,
  Crown,
  CreditCard
} from "lucide-react";

const COACHING_SUBJECTS = [
  "Leadership Resilience",
  "Sabbatical Planning & Re-entry",
  "Church Staffing & Team Leadership",
  "Succession & Transition Planning",
  "Church Planting & Launch",
  "Spiritual Formation & Soul Care",
  "Conflict Resolution & Reconciliation",
  "Vision Casting & Strategic Planning",
  "Non-Profit Leadership & Governance",
  "Fundraising & Stewardship",
  "Multisite & Church Growth",
  "Revitalization & Turnaround",
  "Bi-vocational Ministry",
  "Cross-Cultural & Missions Leadership",
  "Board & Elder Relations"
];

const COACHING_STYLES = [
  "Supportive & Encouraging",
  "Direct & Challenging",
  "Analytical & Data-Driven",
  "Intuitive & Exploratory",
  "Structured & Goal-Oriented"
];

const CoachDashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [coachData, setCoachData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editedProfile, setEditedProfile] = useState({});
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Preferred coach state
  const [preferredApplication, setPreferredApplication] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const coachToken = localStorage.getItem("coach_token");

  useEffect(() => {
    if (!coachToken) {
      navigate("/coach-login");
      return;
    }
    fetchCoachData();
    
    // Check for payment return
    const paymentStatus = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    if (paymentStatus === "success" && sessionId) {
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === "cancelled") {
      toast.error("Payment was cancelled");
    }
  }, [coachToken]);

  const fetchCoachData = async () => {
    try {
      const [meRes, profileRes, conversationsRes, preferredAppRes, subscriptionRes] = await Promise.all([
        axios.get(`${API}/auth/coach/me`, {
          headers: { Authorization: `Bearer ${coachToken}` }
        }),
        axios.get(`${API}/coach/profile`, {
          headers: { Authorization: `Bearer ${coachToken}` }
        }).catch(() => null),
        axios.get(`${API}/coach/conversations`, {
          headers: { Authorization: `Bearer ${coachToken}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/preferred-coach/application`, {
          headers: { Authorization: `Bearer ${coachToken}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/preferred-coach/subscription-status`, {
          headers: { Authorization: `Bearer ${coachToken}` }
        }).catch(() => ({ data: null }))
      ]);
      
      setCoachData(meRes.data);
      if (profileRes) {
        setProfile(profileRes.data);
        setEditedProfile(profileRes.data);
      }
      setMessages(conversationsRes.data || []);
      setPreferredApplication(preferredAppRes.data);
      setSubscriptionStatus(subscriptionRes.data);
    } catch (error) {
      console.error("Error fetching coach data:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("coach_token");
        navigate("/coach-login");
      }
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error("Payment status check timed out. Please refresh the page.");
      return;
    }

    try {
      const response = await axios.get(`${API}/preferred-coach/checkout-status/${sessionId}`, {
        headers: { Authorization: `Bearer ${coachToken}` }
      });

      if (response.data.status === "paid") {
        toast.success("Payment successful! Your preferred status is now active.");
        fetchCoachData(); // Refresh data
        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      // Continue polling if still pending
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      toast.error("Error checking payment status");
    }
  };

  const handleStartSubscription = async (planType) => {
    setProcessingPayment(true);
    setSelectedPlan(planType);
    try {
      const response = await axios.post(
        `${API}/preferred-coach/create-checkout`,
        {
          plan_type: planType,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${coachToken}` } }
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setProcessingPayment(false);
      setSelectedPlan(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("coach_token");
    navigate("/");
    toast.success("Logged out successfully");
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const response = await axios.put(
        `${API}/coach/profile`,
        {
          bio: editedProfile.bio,
          specializations: editedProfile.specializations,
          coaching_style: editedProfile.coaching_style,
          availability: editedProfile.availability,
          photo_url: editedProfile.photo_url,
          hourly_rate: editedProfile.hourly_rate,
          enneagram: editedProfile.enneagram,
          myers_briggs: editedProfile.myers_briggs,
          strengths_finder: editedProfile.strengths_finder,
          primal_question: editedProfile.primal_question,
          booking_link: editedProfile.booking_link
        },
        { headers: { Authorization: `Bearer ${coachToken}` } }
      );
      setProfile(response.data);
      setEditingProfile(false);
      toast.success("Profile updated successfully!");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const openReplyDialog = (message) => {
    setSelectedMessage(message);
    setReplyText(message.coach_reply || "");
    setReplyDialogOpen(true);
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      toast.error("Please enter a reply");
      return;
    }

    setSendingReply(true);
    try {
      await axios.post(
        `${API}/coach/conversations/${selectedMessage.id}/reply`,
        { reply: replyText },
        { headers: { Authorization: `Bearer ${coachToken}` } }
      );
      
      // Refresh conversations
      fetchCoachData();
      
      setReplyDialogOpen(false);
      toast.success("Reply sent successfully!");
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setSendingReply(false);
    }
  };

  const toggleSpecialization = (spec) => {
    const current = editedProfile.specializations || [];
    if (current.includes(spec)) {
      setEditedProfile({
        ...editedProfile,
        specializations: current.filter(s => s !== spec)
      });
    } else {
      setEditedProfile({
        ...editedProfile,
        specializations: [...current, spec]
      });
    }
  };

  const toggleCoachingStyle = (style) => {
    const current = editedProfile.coaching_style || [];
    if (current.includes(style)) {
      setEditedProfile({
        ...editedProfile,
        coaching_style: current.filter(s => s !== style)
      });
    } else {
      setEditedProfile({
        ...editedProfile,
        coaching_style: [...current, style]
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="animate-pulse text-[#7C9A92] text-xl">Loading...</div>
      </div>
    );
  }

  const pendingMessages = messages.filter(m => m.status === "pending");

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Leaf className="w-8 h-8 text-[#7C9A92]" />
            <span className="font-serif text-2xl font-medium text-[#2C332B]">CoachBridge</span>
          </Link>

          <div className="flex items-center gap-4">
            <span className="hidden md:block text-[#78716C]">
              Coach: {coachData?.first_name} {coachData?.last_name}
            </span>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-[#78716C] hover:text-[#2C332B]"
              data-testid="coach-logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-serif text-4xl md:text-5xl font-light text-[#2C332B] mb-4">
            Welcome, {coachData?.first_name}
          </h1>
          <p className="text-lg text-[#78716C]">
            Manage your coaching profile and respond to client inquiries
          </p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#7C9A92]/10 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-[#7C9A92]" />
                </div>
                <div>
                  <p className="text-sm text-[#78716C]">Total Messages</p>
                  <p className="text-2xl font-semibold text-[#2C332B]">{messages.length}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <Card>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#E09F7D]/10 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#E09F7D]" />
                </div>
                <div>
                  <p className="text-sm text-[#78716C]">Pending Replies</p>
                  <p className="text-2xl font-semibold text-[#2C332B]">{pendingMessages.length}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#7C9A92]/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-[#7C9A92]" />
                </div>
                <div>
                  <p className="text-sm text-[#78716C]">Hourly Rate</p>
                  <p className="text-2xl font-semibold text-[#2C332B]">
                    ${profile?.hourly_rate || 0}/hr
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="messages" className="space-y-6">
          <TabsList className="bg-white border border-stone-100 p-1 rounded-xl">
            <TabsTrigger 
              value="messages" 
              className="rounded-lg data-[state=active]:bg-[#7C9A92] data-[state=active]:text-white"
              data-testid="messages-tab"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Messages
              {pendingMessages.length > 0 && (
                <Badge className="ml-2 bg-[#E09F7D] text-white">
                  {pendingMessages.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="profile"
              className="rounded-lg data-[state=active]:bg-[#7C9A92] data-[state=active]:text-white"
              data-testid="profile-tab"
            >
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger 
              value="preferred"
              className="rounded-lg data-[state=active]:bg-[#7C9A92] data-[state=active]:text-white"
              data-testid="preferred-tab"
            >
              <Crown className="w-4 h-4 mr-2" />
              Preferred
              {subscriptionStatus?.is_preferred && (
                <Badge className="ml-2 bg-[#E09F7D] text-white text-xs">Active</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Conversations Tab */}
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#7C9A92]" />
                  Client Conversations
                  {messages.filter(c => c.unread_by_coach).length > 0 && (
                    <Badge className="ml-2 bg-[#E09F7D] text-white">
                      {messages.filter(c => c.unread_by_coach).length} new
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {messages.length > 0 ? (
                  <div className="space-y-4">
                    {messages.map((conv) => (
                      <div
                        key={conv.id}
                        className={`p-4 rounded-xl border ${
                          conv.unread_by_coach 
                            ? "border-[#E09F7D]/50 bg-[#E09F7D]/5" 
                            : "border-stone-100 bg-white"
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-semibold text-[#2C332B]">{conv.user_name}</p>
                            <p className="text-sm text-[#78716C]">{conv.user_email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant={conv.unread_by_coach ? "default" : "secondary"}
                              className={conv.unread_by_coach ? "bg-[#E09F7D]" : ""}
                            >
                              {conv.messages?.length || 0} messages
                            </Badge>
                            <span className="text-xs text-[#78716C]">
                              {new Date(conv.updated_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        
                        {/* Messages Thread Preview */}
                        <div className="space-y-2 mb-3 max-h-[200px] overflow-y-auto">
                          {conv.messages?.slice(-3).map((msg, idx) => (
                            <div 
                              key={msg.id || idx}
                              className={`p-2 rounded-lg text-sm ${
                                msg.sender_type === "user" 
                                  ? "bg-stone-50" 
                                  : "bg-[#7C9A92]/10 ml-4"
                              }`}
                            >
                              <span className="text-xs font-medium text-[#78716C]">
                                {msg.sender_type === "user" ? conv.user_name : "You"}:
                              </span>
                              <p className="text-[#2C332B]">{msg.message}</p>
                            </div>
                          ))}
                        </div>

                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => openReplyDialog(conv)}
                          className="rounded-full bg-[#7C9A92] hover:bg-[#6B8A82]"
                          data-testid={`reply-btn-${conv.id}`}
                        >
                          Reply
                          <Send className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                    <p className="text-[#78716C]">No conversations yet. New inquiries will appear here.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <User className="w-5 h-5 text-[#7C9A92]" />
                  Your Profile
                </CardTitle>
                {!editingProfile ? (
                  <Button
                    variant="outline"
                    onClick={() => setEditingProfile(true)}
                    className="rounded-full"
                    data-testid="edit-profile-btn"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingProfile(false);
                        setEditedProfile(profile);
                      }}
                      className="rounded-full"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveProfile}
                      disabled={savingProfile}
                      className="rounded-full bg-[#7C9A92] hover:bg-[#6B8A82]"
                      data-testid="save-profile-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {savingProfile ? "Saving..." : "Save"}
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-6">
                {profile ? (
                  <>
                    {/* Basic Info - Not editable */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label className="text-[#78716C]">Name</Label>
                        <p className="text-lg font-medium text-[#2C332B]">
                          {profile.first_name} {profile.last_name}
                        </p>
                      </div>
                      <div>
                        <Label className="text-[#78716C]">Email</Label>
                        <p className="text-lg font-medium text-[#2C332B]">{profile.email}</p>
                      </div>
                    </div>

                    {/* Photo URL */}
                    <div className="space-y-2">
                      <Label className="text-[#2C332B] font-medium">Profile Photo URL</Label>
                      {editingProfile ? (
                        <Input
                          value={editedProfile.photo_url || ""}
                          onChange={(e) => setEditedProfile({
                            ...editedProfile,
                            photo_url: e.target.value
                          })}
                          placeholder="https://example.com/photo.jpg"
                          className="h-12 rounded-xl"
                        />
                      ) : (
                        <div className="flex items-center gap-4">
                          {profile.photo_url ? (
                            <>
                              <img 
                                src={profile.photo_url} 
                                alt="Profile" 
                                className="w-16 h-16 rounded-full object-cover"
                              />
                              <p className="text-[#78716C] text-sm truncate max-w-md">
                                {profile.photo_url}
                              </p>
                            </>
                          ) : (
                            <p className="text-[#78716C]">No photo set</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Bio */}
                    <div className="space-y-2">
                      <Label className="text-[#2C332B] font-medium">Bio</Label>
                      {editingProfile ? (
                        <Textarea
                          value={editedProfile.bio || ""}
                          onChange={(e) => setEditedProfile({
                            ...editedProfile,
                            bio: e.target.value
                          })}
                          placeholder="Tell potential clients about yourself..."
                          className="min-h-[120px] rounded-xl"
                        />
                      ) : (
                        <p className="text-[#2C332B] p-4 bg-stone-50 rounded-xl">
                          {profile.bio || "No bio set"}
                        </p>
                      )}
                    </div>

                    {/* Hourly Rate & Availability */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-[#2C332B] font-medium">Hourly Rate ($)</Label>
                        {editingProfile ? (
                          <Input
                            type="number"
                            value={editedProfile.hourly_rate || ""}
                            onChange={(e) => setEditedProfile({
                              ...editedProfile,
                              hourly_rate: parseFloat(e.target.value) || 0
                            })}
                            className="h-12 rounded-xl"
                          />
                        ) : (
                          <p className="text-lg font-medium text-[#2C332B]">
                            ${profile.hourly_rate || 0}/hr
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#2C332B] font-medium">Availability</Label>
                        {editingProfile ? (
                          <Select
                            value={editedProfile.availability || ""}
                            onValueChange={(v) => setEditedProfile({
                              ...editedProfile,
                              availability: v
                            })}
                          >
                            <SelectTrigger className="h-12 rounded-xl">
                              <SelectValue placeholder="Select availability" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Weekday mornings">Weekday mornings</SelectItem>
                              <SelectItem value="Weekday afternoons">Weekday afternoons</SelectItem>
                              <SelectItem value="Weekday evenings">Weekday evenings</SelectItem>
                              <SelectItem value="Weekends">Weekends</SelectItem>
                              <SelectItem value="Flexible">Flexible</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="text-lg font-medium text-[#2C332B]">
                            {profile.availability || "Not set"}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Booking Link */}
                    <div className="space-y-2">
                      <Label className="text-[#2C332B] font-medium flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#7C9A92]" />
                        Free Consultation Booking Link
                      </Label>
                      <p className="text-xs text-[#78716C] mb-2">
                        Add your Calendly or Cal.com link so users can book a free 15-minute consultation
                      </p>
                      {editingProfile ? (
                        <Input
                          value={editedProfile.booking_link || ""}
                          onChange={(e) => setEditedProfile({
                            ...editedProfile,
                            booking_link: e.target.value
                          })}
                          placeholder="https://calendly.com/yourname/15min"
                          className="h-12 rounded-xl"
                        />
                      ) : (
                        <p className="text-[#2C332B]">
                          {profile.booking_link ? (
                            <a href={profile.booking_link} target="_blank" rel="noopener noreferrer" className="text-[#7C9A92] hover:underline">
                              {profile.booking_link}
                            </a>
                          ) : (
                            <span className="text-[#78716C]">No booking link set</span>
                          )}
                        </p>
                      )}
                    </div>

                    {/* Specializations */}
                    <div className="space-y-3">
                      <Label className="text-[#2C332B] font-medium">Specializations</Label>
                      {editingProfile ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {COACHING_SUBJECTS.map((spec) => (
                            <button
                              key={spec}
                              type="button"
                              onClick={() => toggleSpecialization(spec)}
                              className={`p-2 rounded-lg border text-sm font-medium transition-all text-left ${
                                editedProfile.specializations?.includes(spec)
                                  ? "bg-[#7C9A92] text-white border-[#7C9A92]"
                                  : "bg-white text-[#2C332B] border-stone-200 hover:border-[#7C9A92]/50"
                              }`}
                            >
                              {spec}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {profile.specializations?.length > 0 ? (
                            profile.specializations.map((spec) => (
                              <Badge key={spec} className="bg-[#7C9A92]">{spec}</Badge>
                            ))
                          ) : (
                            <p className="text-[#78716C]">No specializations set</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Coaching Style */}
                    <div className="space-y-3">
                      <Label className="text-[#2C332B] font-medium">Coaching Style</Label>
                      {editingProfile ? (
                        <div className="flex flex-wrap gap-2">
                          {COACHING_STYLES.map((style) => (
                            <button
                              key={style}
                              type="button"
                              onClick={() => toggleCoachingStyle(style)}
                              className={`px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                                editedProfile.coaching_style?.includes(style)
                                  ? "bg-[#E09F7D] text-white border-[#E09F7D]"
                                  : "bg-white text-[#2C332B] border-stone-200 hover:border-[#E09F7D]/50"
                              }`}
                            >
                              {style}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {profile.coaching_style?.length > 0 ? (
                            profile.coaching_style.map((style) => (
                              <Badge key={style} variant="outline" className="border-[#E09F7D] text-[#E09F7D]">
                                {style}
                              </Badge>
                            ))
                          ) : (
                            <p className="text-[#78716C]">No coaching style set</p>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <User className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                    <p className="text-[#78716C]">Profile not found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Preferred Status Tab */}
          <TabsContent value="preferred">
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Crown className="w-5 h-5 text-[#E09F7D]" />
                  Preferred Coach Status
                </CardTitle>
                <CardDescription>
                  Get premium visibility and appear first in search results
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current Status */}
                {subscriptionStatus?.is_preferred ? (
                  <div className="bg-gradient-to-r from-[#7C9A92]/10 to-[#E09F7D]/10 rounded-xl p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#7C9A92] to-[#E09F7D] flex items-center justify-center">
                        <Star className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-[#2C332B]">You're a Preferred Coach!</h3>
                        <p className="text-[#78716C]">Your profile appears first in search results</p>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div className="bg-white/50 rounded-lg p-3">
                        <p className="text-[#78716C]">Plan</p>
                        <p className="font-medium text-[#2C332B] capitalize">{subscriptionStatus.plan_type || "Active"}</p>
                      </div>
                      {subscriptionStatus.expires_at && (
                        <div className="bg-white/50 rounded-lg p-3">
                          <p className="text-[#78716C]">Renews</p>
                          <p className="font-medium text-[#2C332B]">
                            {new Date(subscriptionStatus.expires_at).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : preferredApplication?.status === "approved" ? (
                  /* Approved - Ready to Subscribe */
                  <div className="space-y-6">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                      <div className="flex gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
                        <div>
                          <p className="font-medium text-green-800">Application Approved!</p>
                          <p className="text-sm text-green-700">Complete your subscription to activate preferred status.</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div 
                        className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                          selectedPlan === "monthly" ? "border-[#7C9A92] bg-[#7C9A92]/5" : "border-stone-200 hover:border-[#7C9A92]/50"
                        }`}
                        onClick={() => !processingPayment && setSelectedPlan("monthly")}
                      >
                        <h4 className="font-medium text-[#2C332B]">Monthly</h4>
                        <p className="text-3xl font-bold text-[#2C332B] mt-2">$49<span className="text-base font-normal text-[#78716C]">/mo</span></p>
                        <p className="text-sm text-[#78716C] mt-2">Cancel anytime</p>
                        <Button
                          onClick={() => handleStartSubscription("monthly")}
                          disabled={processingPayment}
                          className="w-full mt-4 rounded-full bg-[#7C9A92] hover:bg-[#6B8A82]"
                          data-testid="subscribe-monthly-btn"
                        >
                          {processingPayment && selectedPlan === "monthly" ? "Processing..." : "Subscribe Monthly"}
                        </Button>
                      </div>

                      <div 
                        className={`p-6 rounded-xl border-2 cursor-pointer transition-all relative ${
                          selectedPlan === "yearly" ? "border-[#E09F7D] bg-[#E09F7D]/5" : "border-stone-200 hover:border-[#E09F7D]/50"
                        }`}
                        onClick={() => !processingPayment && setSelectedPlan("yearly")}
                      >
                        <Badge className="absolute -top-3 right-4 bg-[#E09F7D]">Save $88</Badge>
                        <h4 className="font-medium text-[#2C332B]">Yearly</h4>
                        <p className="text-3xl font-bold text-[#2C332B] mt-2">$500<span className="text-base font-normal text-[#78716C]">/yr</span></p>
                        <p className="text-sm text-[#78716C] mt-2">Best value</p>
                        <Button
                          onClick={() => handleStartSubscription("yearly")}
                          disabled={processingPayment}
                          className="w-full mt-4 rounded-full bg-[#E09F7D] hover:bg-[#d08a68]"
                          data-testid="subscribe-yearly-btn"
                        >
                          {processingPayment && selectedPlan === "yearly" ? "Processing..." : "Subscribe Yearly"}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : preferredApplication ? (
                  /* Application in progress */
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#7C9A92]/10 flex items-center justify-center">
                      <Clock className="w-8 h-8 text-[#7C9A92]" />
                    </div>
                    <h3 className="text-lg font-medium text-[#2C332B] mb-2">Application {preferredApplication.status.replace(/_/g, " ")}</h3>
                    <p className="text-[#78716C] max-w-md mx-auto">
                      {preferredApplication.status === "pending_review" && "Your application is being reviewed. We'll notify you via email."}
                      {preferredApplication.status === "interview_requested" && "We'd like to schedule an interview. Please check your email."}
                      {preferredApplication.status === "interview_completed" && "Thank you for completing the interview. We're making a final decision."}
                      {preferredApplication.status === "rejected" && (preferredApplication.interview_notes || "Your application was not approved at this time.")}
                    </p>
                    <Badge 
                      className={`mt-4 ${
                        preferredApplication.status === "rejected" ? "bg-red-500" : "bg-[#7C9A92]"
                      }`}
                    >
                      {preferredApplication.status.replace(/_/g, " ").toUpperCase()}
                    </Badge>
                  </div>
                ) : (
                  /* No application - CTA to apply */
                  <div className="space-y-6">
                    <div className="text-center py-4">
                      <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#7C9A92]/20 to-[#E09F7D]/20 flex items-center justify-center">
                        <Crown className="w-10 h-10 text-[#E09F7D]" />
                      </div>
                      <h3 className="text-xl font-semibold text-[#2C332B] mb-2">Become a Preferred Coach</h3>
                      <p className="text-[#78716C] max-w-md mx-auto">
                        Stand out from the crowd with premium visibility and priority placement in search results.
                      </p>
                    </div>

                    <div className="grid gap-3">
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-stone-50">
                        <CheckCircle className="w-5 h-5 text-[#7C9A92]" />
                        <span className="text-sm text-[#2C332B]">Appear first in recommended coaches</span>
                      </div>
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-stone-50">
                        <CheckCircle className="w-5 h-5 text-[#7C9A92]" />
                        <span className="text-sm text-[#2C332B]">Verified preferred badge on your profile</span>
                      </div>
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-stone-50">
                        <CheckCircle className="w-5 h-5 text-[#7C9A92]" />
                        <span className="text-sm text-[#2C332B]">Enhanced trust through additional vetting</span>
                      </div>
                    </div>

                    <div className="bg-[#F5F1E8] rounded-xl p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium text-[#2C332B]">$49/month or $500/year</p>
                          <p className="text-sm text-[#78716C]">Cancel anytime</p>
                        </div>
                        <Button
                          onClick={() => navigate("/preferred-coach-apply")}
                          className="rounded-full bg-[#7C9A92] hover:bg-[#6B8A82]"
                          data-testid="apply-preferred-btn"
                        >
                          Apply Now
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Reply to {selectedMessage?.user_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedMessage && (
            <div className="space-y-4 mt-4">
              <div className="bg-stone-50 rounded-xl p-4">
                <p className="text-xs text-[#78716C] mb-1">Original Message:</p>
                <p className="text-sm text-[#2C332B]">{selectedMessage.message}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Your Reply</Label>
                <Textarea
                  placeholder="Share your contact information and availability for a free consultation..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  className="min-h-[150px] rounded-xl"
                  data-testid="reply-textarea"
                />
              </div>

              <p className="text-xs text-[#78716C]">
                Remember: All CoachBridge coaches offer a free 15-minute consultation. Include your preferred contact method and availability.
              </p>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setReplyDialogOpen(false)}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendReply}
                  disabled={sendingReply}
                  className="flex-1 rounded-full bg-[#7C9A92] hover:bg-[#6B8A82]"
                  data-testid="send-reply-btn"
                >
                  {sendingReply ? "Sending..." : "Send Reply"}
                  <Send className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CoachDashboardPage;
